/* eslint-disable react/no-array-index-key */
import React from 'react';
import BaseInputLayout, { COMMON_INPUT_LAYOUT_DEFAULT_PROPS, getBaseInputLayoutProps } from './BaseInputLayout';
import { FormFieldsData, InputListLayoutProps, InputListLayoutType } from 'framework/form';
import AddIcon from '@mui/icons-material/AddOutlined';
import RemoveIcon from '@mui/icons-material/RemoveOutlined';
import Box from '@mui/material/Box';
import { Tooltip, Button } from '../../../mui-builder';
import type { SxProps, Theme } from '@mui/material';

const inputListRowSx: SxProps<Theme> = theme => ({
  display: 'flex',
  mb: '15px',
  '&:first-of-type': { mt: '8px' },
  '&:last-of-type': { mb: 0 },
  '& .form-group': {
    flexGrow: 1,
    mt: 0,
    mb: '28px',
  },
  '& .react-autosuggest__container': {
    flexGrow: 1,
    width: '100%',
  },
  '& .milestone-input': {
    ml: '16px',
    '&.milestone-input--estimated': { maxWidth: '150px' },
  },
  '& .add-info-btn': {
    mt: '29px',
    ml: '20px',
    minWidth: '1px',
    alignSelf: 'flex-start',
    '& .MuiSvgIcon-root': {
      width: '0.75em',
      height: '0.75em',
    },
  },
  '&:not(:has(div.label__wrapper))': {
    '& .add-info-btn': { mt: '6px' },
  },
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    mb: '40px',
    '&:last-of-type': { mb: '25px' },
    '& .add-info-btn': {
      mt: 0,
      ml: 0,
      width: '100%',
    },
    '&:not(:has(div.label__wrapper))': {
      '& .MuiFormControl-root': { mb: '8px' },
      '& .add-info-btn': { mt: '6px' },
      '&:not(:last-of-type)': { mb: '25px' },
    },
  },
});

/**
 * Purpose of this method:
 * Behavior: Added inputs should be visible, but not included in value
 * until something is entered
 * Problem: Since value doesn't include some inputs from view, this inputs not validated
 * e.g. there are 2 inputs on the view, and only 2nd one filled in with data
 * in this case validation method have no information about view, therefore validation message for second
 * input will be located at index 0.
 * Solution: calculate index in validation messages by ignoring null-values
 * @param value
 * @param index
 * @returns {number}
 */
export const getNotNullIndex = (value: (string | object)[], index: number): number => {
  let notNullIndex = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < index; i++) {
    if (value[i]) {
      // eslint-disable-next-line no-plusplus
      notNullIndex++;
    }
  }

  return notNullIndex;
};

const InputListLayout: InputListLayoutType = ({
  fields,
  addMethod,
  removeMethod,
  inputTemplate: InputComponent,
  onChangeCallback,
  addText = '',
  deleteText = '',
  maxFields,
  minFields,
  source,
  className,
  simpleLayout = true,
  inputProps,
  value,
  disabled,
  disabledRemove,
  isAddable = true,
  isDeletable = true,
  wrapperClassName,
  rowClassName,
  rowSx,
  removeButtonTooltipe,
  ...otherProps
}: InputListLayoutProps) => {
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    ...otherProps,
  };
  const removeBtn = (index: number, fieldValue: any) => {
    return (
      <Tooltip title={removeButtonTooltipe && removeButtonTooltipe(fieldValue)}>
        <span>
          <Button
            id={`remove-${index}`}
            size="small"
            color="primary"
            onClick={() => removeMethod(index)}
            disabled={fields.length <= minFields || disabled || (disabledRemove && disabledRemove(fieldValue))}
            className="add-info-btn input-list-rm-btn"
            sx={{ padding: deleteText ? '5px 17px 5px 10px' : '4px' }}
          >
            <RemoveIcon sx={{ marginRight: deleteText ? '7px' : 0 }} />
            {deleteText}
          </Button>
        </span>
      </Tooltip>
    );
  };

  // TODO: temporary logs
  if (!InputComponent) {
    // eslint-disable-next-line no-console
    console.error('There are no InputComponent in ', source);
  }

  return (
    <BaseInputLayout {...getBaseInputLayoutProps({ ...props, source, disabled })}>
      <div className={wrapperClassName}>
        {fields.map((fieldValue, index) => (
          <Box
            className={`removable-input ${rowClassName || ''}`}
            key={`input-${index}`}
            sx={[inputListRowSx, ...(Array.isArray(rowSx) ? rowSx : [rowSx])]}
          >
            {!simpleLayout && isDeletable && removeBtn(index, fieldValue)}
            <InputComponent
              {...inputProps}
              label=""
              isLast={fields.length === index + 1}
              source={fieldValue && `${source}.${getNotNullIndex(fields, index)}`}
              index={index}
              value={fieldValue}
              onChangeCallback={(newValue: FormFieldsData) => {
                onChangeCallback(index, newValue);
              }}
              className={className || ''}
            />
            {simpleLayout && isDeletable && removeBtn(index, fieldValue)}
          </Box>
        ))}
        {isAddable && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            id="add"
            disabled={Boolean((maxFields && fields.length >= maxFields) || disabled)}
            type="button"
            onClick={addMethod}
            sx={{
              paddingRight: addText ? '14px' : '9px',
              '& .MuiButton-startIcon': addText
                ? {}
                : {
                    margin: 0,
                  },
            }}
          >
            {addText}
          </Button>
        )}
      </div>
    </BaseInputLayout>
  );
};

export default InputListLayout;
